import React, { useMemo } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import TwoColumn, { Alignments } from '@components/shared/TwoColumn';
import { PageSlice, SlicePriorityTipsDetails, SlicePriorityTipsItem } from '@contracts';
import useDesktop from './useDesktop';

const PRIORITY_COLORS: Record<string, { background: string; color: string }> = {
  low: {
    background: Colors.sage,
    color: Colors.navyBlue
  },
  medium: {
    background: Colors.gold,
    color: Colors.cream
  },
  high: {
    background: Colors.red,
    color: Colors.cream
  }
};

interface Props {
  slice: PageSlice;
}

const PriorityTips = ({ slice }: Props) => {
  const isDesktop = useDesktop(768);
  const sliceDetails = slice.primary as SlicePriorityTipsDetails;
  const sliceItems = slice.items as SlicePriorityTipsItem[];
  const [tips, priorities] = useMemo(
    () =>
      (sliceItems || []).reduce(
        ([tips, priorities], item) => {
          if (item.type === 'tip') {
            tips.push(item);
          } else if (item.type === 'priority') {
            priorities.push(item);
          }
          return [tips, priorities];
        },
        [[] as SlicePriorityTipsItem[], [] as SlicePriorityTipsItem[]]
      ),
    [sliceItems]
  );

  const renderTipsView = () => (
    <TipsWrap>
      {tips.map((tip) => (
        <Tip key={tip.text?.text}>
          <TipImageWrap>{tip.image ? <GatsbyImage image={tip.image.gatsbyImageData} alt={tip.image.alt || tip.text?.text} /> : null}</TipImageWrap>
          <TipTextWrap>
            <TipText>{tip.text.text}</TipText>
          </TipTextWrap>
        </Tip>
      ))}
    </TipsWrap>
  );

  const renderBottomInfo = () => (
    <BottomInfoWrap>
      <BottomInfoImageWrap>
        {sliceDetails?.bottom_info_image ? (
          <GatsbyImage
            image={sliceDetails.bottom_info_image.gatsbyImageData}
            alt={sliceDetails.bottom_info_image.alt || sliceDetails?.heading?.text}
          />
        ) : null}
      </BottomInfoImageWrap>
      <BottomInfoTextWrap>
        <BottomInfoText>{sliceDetails?.bottom_info?.text}</BottomInfoText>
      </BottomInfoTextWrap>
    </BottomInfoWrap>
  );

  const renderPriorities = () => (
    <PriorityColumnWrap>
      {priorities.map((priority) => (
        <PriorityWrap key={priority.text?.text}>
          <PriorityIconWrap>
            {priority.image ? <GatsbyImage image={priority.image.gatsbyImageData} alt={priority.image.alt || priority.text?.text} /> : null}
          </PriorityIconWrap>
          <PriorityTextWrap>
            <PriorityText>{priority.text?.text}</PriorityText>
          </PriorityTextWrap>
          <PriorityBadgeWrap>
            <PriorityBadge $priority={priority.priority}>{priority.priority}</PriorityBadge>
          </PriorityBadgeWrap>
        </PriorityWrap>
      ))}
    </PriorityColumnWrap>
  );

  return (
    <Container $padding={sliceDetails?.padding?.text} $background={sliceDetails?.background?.text}>
      <Inner>
        {isDesktop ? (
          <TwoColumn gap="20px" breakpoint="768px" alignment={Alignments.Top}>
            <TipColumnWrap>
              <Heading>{sliceDetails?.heading?.text}</Heading>
              {renderTipsView()}
              {renderBottomInfo()}
            </TipColumnWrap>
            {renderPriorities()}
          </TwoColumn>
        ) : (
          <ColumnWrap>
            <Heading>{sliceDetails?.heading?.text}</Heading>
            {renderPriorities()}
            {renderTipsView()}
            {renderBottomInfo()}
          </ColumnWrap>
        )}
      </Inner>
    </Container>
  );
};

const Container = styled.div<{ $padding: string; $background: string }>`
  background: ${(props) => props.$background};
  padding: ${(props) => props.$padding || '60px'};
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    padding: 40px 30px;
  }
`;

const Inner = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const TipColumnWrap = styled.div`
  padding: 40px;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const Heading = styled.div`
  margin-top: 10px;
  font-family: 'Domaine';
  font-weight: 600;
  font-size: 30px;
  line-height: 120%;
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    margin-top: 0;
    text-align: center;
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

const TipsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin: 30px 0;
`;

const Tip = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const TipImageWrap = styled.div`
  flex: 0 0 auto;
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const TipTextWrap = styled.div`
  flex: 1 1 auto;
`;

const TipText = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const BottomInfoWrap = styled.div`
  background: ${Colors.mint}50;
  padding: 15px 20px;
  border-radius: 2px;
  display: flex;
  gap: 15px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const BottomInfoImageWrap = styled.div`
  flex: 0 0 auto;
`;

const BottomInfoTextWrap = styled.div`
  flex: 1 1 auto;
`;

const BottomInfoText = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const PriorityColumnWrap = styled.div`
  padding: 40px;
  background: ${Colors.mint}50;
  border: 1px solid ${Colors.mint};
  border-radius: 20px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 28px;
  }
`;

const PriorityWrap = styled.div`
  display: flex;
  align-content: center;
  justify-content: space-between;
  gap: 15px;
  background: ${Colors.cream};
  border: 1px solid ${Colors.mint};
  min-width: 350px;
  padding: 24px 25px;

  &:first-child {
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  &:last-child {
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  &:not(:first-child) {
    border-top: none;
  }

  @media (max-width: 768px) {
    min-width: auto;
    padding: 13px 16px;
  }
`;

const PriorityIconWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    min-width: auto;
    width: 13px;
  }
`;

const PriorityTextWrap = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
`;

const PriorityBadgeWrap = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
`;

const PriorityText = styled.div`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const PriorityBadge = styled.div<{ $priority: string }>`
  display: inline-block;
  font-family: BrownStd;
  font-weight: 700;
  font-size: 11px;
  line-height: 13px;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: ${(props) => PRIORITY_COLORS[props.$priority]?.background};
  color: ${(props) => PRIORITY_COLORS[props.$priority]?.color};
  padding: 5px 8px;
  border-radius: 2px;

  @media (max-width: 768px) {
    font-size: 7px;
    line-height: 8px;
    padding: 3px 5px;
  }
`;

const ColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

export default PriorityTips;
